/* eslint-disable max-lines */
import React, { useEffect, useRef, useState, useContext } from 'react'
import { CheckIcon } from '@chakra-ui/icons'
import { Button, HStack, Input, Text, VStack, Icon as CkIcon } from '@chakra-ui/react'
import { EZIndexLayer } from 'enums/theme'
import get from 'lodash/get'
import set from 'lodash/set'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import { UploaderIcon } from 'theme/icon'
import { CommonError } from 'types'
import { handleError } from 'API/error'
import { uploadFile } from 'API/fileUploader'
import Icon from 'components/Icon'
import { IApplicationFormValues } from 'interfaces/application'
import { getValidArray, isHeicFile } from 'utils/common'
import { ApplicationContext } from '../../../../applicationForm.context'
import { BottomBackButton, BottomButton } from '../../../../applicationFormModalV2.styles'
import { DESKTOP_CONTAINER_WIDTH, EBottomButtonValue } from '../../../../constant'
import { PAYSTUBS_REQUIRED_QUANTITY } from '../../../../constant'
import { notifyPaystubsRequiredQuantity } from '../../../../utils'
import { CompleteScreeningContext } from '../../completeScreening.context'
import { handleValidateFiles, shortenFileName } from '../../utils'
import PreviousLandlordInformation from '../PreviousLandlordInformation'
import { validatePaystubStep } from './utils'

export interface IUploadPaystubProps {
  paystubFiles: string[]
}

const PayStubUpload = (props: IUploadPaystubProps) => {
  const { paystubFiles } = props
  const filePickerRef = useRef<HTMLInputElement | null>(null)
  const [fileList, setFileList] = useState<string[]>([])
  const { setValue, getValues, trigger } = useFormContext()
  const formData: IApplicationFormValues = getValues()
  const { setIsLoading, setIsBackLoading, isLoading, isBackLoading, nextStep } = useContext(ApplicationContext)
  const { handlePreviousStep } = useContext(CompleteScreeningContext)

  function handleClickUpload(): void {
    const currentRef = filePickerRef.current || { click: () => {} }
    currentRef.click()
  }

  function handleSubmit(): void {
    if (getValidArray(fileList).length < PAYSTUBS_REQUIRED_QUANTITY) {
      notifyPaystubsRequiredQuantity()
    } else if (validatePaystubStep(formData)) {
      typeof nextStep === 'function' && nextStep()
    } else {
      trigger(['previousLandlordName', 'previousLandlordPhoneNumber'])
    }
  }

  useEffect(() => {
    setValue('paystubFiles', fileList)
  }, [fileList])

  useEffect(() => {
    if (getValidArray(paystubFiles).length > 0) {
      setFileList(paystubFiles)
    }
  }, [paystubFiles])

  async function handleUploadFile(file: File): Promise<string> {
    try {
      if (isHeicFile(file?.name ?? '')) {
        const heic2any = require('heic2any')
        const jpgFileBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg'
        })

        const jpgFile = new File([jpgFileBlob as BlobPart], `${file.name.replace(/\.heic$/i, '')}.jpg`, {
          type: 'image/jpeg'
        })
        const imageUrl: string = await uploadFile(jpgFile)
        return imageUrl
      }
      const imageUrl: string = await uploadFile(file)
      return imageUrl
    } catch (error) {
      let errorMessage: string = 'Upload image failed.'
      if ((error as CommonError)?.message) {
        errorMessage += ` ${(error as CommonError)?.message}`
      }
      toast.error(errorMessage)
      return ''
    }
  }

  async function handleChangeFile(event: { target: EventTarget }): Promise<void> {
    try {
      typeof setIsLoading === 'function' && setIsLoading(true)
      typeof setIsBackLoading === 'function' && setIsBackLoading(true)
      const files: File[] = get(event, 'target.files') || []
      const fileArray: File[] = handleValidateFiles(files)

      const result: string[] = await Promise.all(
        getValidArray(fileArray).map(async (file: File) => {
          if (!file?.name) {
            return ''
          }
          const response: string = await handleUploadFile(file as File)
          return response
        })
      )
      const filteredResult: string[] = result.filter((item: string) => !!item)
      setFileList([...fileList, ...filteredResult])
      clearCurrentValue()
      if (filteredResult.length > 0) {
        toast.success('Upload document successfully!')
      }
    } catch (error) {
      const errorMessage: string = get(error, 'message', 'Something went wrong!')
      toast.error(errorMessage)
      handleError(
        error as CommonError,
        './components/pages/DetailPage/components/ApplicationFormModalV2/components/CompleteScreening/components/PaystubUpload',
        'handleChangeFile'
      )
    } finally {
      typeof setIsLoading === 'function' && setIsLoading(false)
      typeof setIsBackLoading === 'function' && setIsBackLoading(false)
    }
  }

  function clearCurrentValue(): void {
    set(filePickerRef, 'current.value', '')
  }

  function deleteFile(index: number): void {
    const newFileList: string[] = getValidArray(fileList).filter((_, i) => i !== index)
    setFileList(newFileList)
  }

  return (
    <VStack
      spacing={8}
      alignItems={{ base: 'flex-start', lg: 'center' }}
      width="full"
      height="full"
      marginBottom={{ base: 20, lg: 'auto' }}
    >
      <VStack
        maxWidth={{ base: 'full', lg: DESKTOP_CONTAINER_WIDTH }}
        marginX="auto"
        minHeight={{ base: 'full', md: 'auto', lg: 'auto' }}
        paddingTop={{ base: 6, lg: 40 }}
        spacing={8}
        paddingX={4}
        alignItems="flex-start"
        width="full"
        height="full"
      >
        <VStack spacing={4} alignItems="flex-start" width="full" paddingBottom={{ lg: 0 }}>
          <Text fontSize="md" fontWeight="500" color="gray.700">
            Please upload <b>at least {PAYSTUBS_REQUIRED_QUANTITY}</b> of your most recent pay stubs or bank statements.
          </Text>
          <VStack spacing={4} alignItems="flex-start" width="full">
            <Button
              flexDirection="row"
              iconSpacing={2}
              leftIcon={<UploaderIcon />}
              variant="outline"
              onClick={handleClickUpload}
              fontSize={{ base: 'lg', lg: 'md' }}
              lineHeight={{ base: 7, lg: 6 }}
              width="full"
              height={{ base: 12, lg: '44px' }}
              color="gray.700"
              justifyContent={{ base: 'center', lg: 'flex-start' }}
              borderRadius="6px"
              isLoading={isLoading}
            >
              Upload Files
            </Button>
            <Input
              type="file"
              ref={filePickerRef}
              onChange={handleChangeFile}
              display="none"
              accept=".svg, .png, .jpg, .jpeg, application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .csv, .doc, .docx, image/heic, image/heif, image/heif-sequence, image/heic-sequence, image/avif"
              multiple
            />
            <VStack spacing={4} alignItems="flex-start" width="full" marginBottom={{ lg: 0 }}>
              {getValidArray(fileList).map((file: string, index: number) => (
                <VStack
                  key={file}
                  height={{ base: 10 }}
                  alignItems="flex-start"
                  spacing={0}
                  border="1px solid #319795"
                  justifyContent="center"
                  borderRadius="4px"
                  position="relative"
                  width={{ base: 'full' }}
                >
                  <HStack justifyContent="space-between" width="100%" paddingX={3}>
                    <Text fontSize="xs" fontWeight={500} lineHeight={4} color="gray.700" marginBottom={{ lg: 0 }}>
                      {shortenFileName(file, 30)}
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        width={6}
                        height={6}
                        minWidth={6}
                        padding={0}
                        borderRadius="50%"
                        background="teal.500"
                        cursor="default"
                        _hover={{ bg: 'teal.500' }}
                      >
                        <CkIcon as={CheckIcon} width={2} height={2} color="white" />
                      </Button>
                      <Button
                        width={6}
                        height={6}
                        minWidth={6}
                        padding={0}
                        borderRadius="50%"
                        background="gray.100"
                        cursor="pointer"
                        onClick={() => deleteFile(index)}
                      >
                        <Icon iconName="trash.svg" size={20} />
                      </Button>
                    </HStack>
                  </HStack>
                </VStack>
              ))}
            </VStack>
          </VStack>
        </VStack>
        <PreviousLandlordInformation />
      </VStack>
      <HStack
        width="full"
        maxWidth={{ base: 'full', lg: DESKTOP_CONTAINER_WIDTH }}
        height="auto"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        paddingBottom={{ base: 6, md: 12, lg: 'auto' }}
        paddingTop={{ base: 4, lg: '34px' }}
        position={{ base: 'fixed', lg: 'relative' }}
        bottom={{ base: 0, lg: 'auto' }}
        marginTop={{ base: 'auto', lg: '0px !important' }}
        zIndex={EZIndexLayer.MODAL}
        paddingX={4}
        backgroundColor={{ base: 'white', lg: 'none' }}
      >
        <BottomBackButton isLoading={isBackLoading} onClick={handlePreviousStep} variant="solid">
          {EBottomButtonValue.BACK}
        </BottomBackButton>

        <BottomButton
          colorScheme="teal"
          variant="solid"
          iconSpacing={2}
          onClick={handleSubmit}
          isLoading={isLoading}
          rightIcon={<CheckIcon />}
        >
          {EBottomButtonValue.SUBMIT}
        </BottomButton>
      </HStack>
    </VStack>
  )
}

export default PayStubUpload
